/**
 * HelpDialog - Keyboard shortcuts reference dialog
 *
 * Shows all available keyboard shortcuts organized by category.
 * Opened via Shift+/ or Help button in sidebar.
 */

import React, { useMemo } from "react";
import { useAtom, useAtomValue } from "jotai";
import { FaQuestionCircle } from "../utils/icons";
import { helpDialogOpenAtom } from "../atoms/measurementAtoms";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/design-system/components/react";
import { SHORTCUT_GROUPS, filterShortcutGroupsBySurfaces } from "../constants/viewerActions";
import { viewerSurfacesAtom } from "../atoms/viewerSurfacesAtom";

export const HelpDialog: React.FC = () => {
  const [isOpen, setIsOpen] = useAtom(helpDialogOpenAtom);
  const activeSurfaces = useAtomValue(viewerSurfacesAtom);
  const groups = useMemo(
    () => filterShortcutGroupsBySurfaces(SHORTCUT_GROUPS, activeSurfaces),
    [activeSurfaces],
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-2xl max-h-[80vh] bg-surface border-border-strong">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FaQuestionCircle className="w-5 h-5 text-signal-text-tint" />
            Keyboard Shortcuts
          </DialogTitle>
        </DialogHeader>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(80vh-140px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {groups.map((category) => (
              <div key={category.category}>
                <h3 className="text-sm font-medium text-signal-text-tint mb-3">
                  {category.category}
                </h3>
                <div className="space-y-2">
                  {category.items.map((item) => (
                    <div key={item.key} className="flex items-center justify-between text-sm gap-4">
                      <kbd className="px-2 py-1 bg-surface-2 rounded text-fg font-mono text-xs whitespace-nowrap">
                        {item.key}
                      </kbd>
                      <span className="text-fg-muted text-right flex-1">{item.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="pt-3 border-t border-border-strong text-center">
          <span className="text-xs text-fg-muted">
            Press{" "}
            <kbd className="px-1.5 py-0.5 bg-surface-2 rounded text-fg-muted font-mono">
              Shift + ?
            </kbd>{" "}
            to open this dialog
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HelpDialog;
